import { useCallback, useMemo, useState } from 'react';
import { useAppSelector } from '../../../../store/store';
import {
  selectCatalogSpecializations,
  selectCatalogDoctors,
  selectSpecDoctorMap,
} from '../../../../store/reducers/SpecializationDoctorCatalogSlice';
import {
  DoctorInfo,
  SpecializationInfo,
} from '../../../../models/Settings/ClinicSettings';

export const useSpecializationDoctors = () => {
  const catalogSpecializations = useAppSelector(selectCatalogSpecializations);
  const catalogDoctors = useAppSelector(selectCatalogDoctors);
  const specDoctorMap = useAppSelector(selectSpecDoctorMap);

  const [excludedSpecializationIds, setExcludedSpecializationIds] = useState<
    string[]
  >([]);
  const [excludedDoctorIds, setExcludedDoctorIds] = useState<string[]>([]);
  const [doctorSearch, setDoctorSearch] = useState('');
  const [specFilter, setSpecFilter] = useState<string | null>(null);

  const getDoctorIdsBySpec = useCallback(
    (specId: string): string[] => specDoctorMap[specId] ?? [],
    [specDoctorMap],
  );

  /** Синхронизирует состояние специализаций после изменения врачей */
  const syncSpecsFromDoctors = useCallback(
    (nextExcludedDoctorIds: string[]) => {
      setExcludedSpecializationIds((prevSpecs) => {
        const nextSpecs = [...prevSpecs];

        for (const spec of catalogSpecializations) {
          const doctorIds = specDoctorMap[spec.id] ?? [];
          if (doctorIds.length === 0) continue;

          const allExcluded = doctorIds.every((id) =>
            nextExcludedDoctorIds.includes(id),
          );
          const specIdx = nextSpecs.indexOf(spec.id);

          if (allExcluded && specIdx === -1) {
            nextSpecs.push(spec.id);
          } else if (!allExcluded && specIdx !== -1) {
            nextSpecs.splice(specIdx, 1);
          }
        }

        return nextSpecs;
      });
    },
    [catalogSpecializations, specDoctorMap],
  );

  const handleToggleSpecialization = useCallback(
    (specId: string) => {
      const doctorIds = getDoctorIdsBySpec(specId);
      const wasExcluded = excludedSpecializationIds.includes(specId);

      setExcludedSpecializationIds((prev) =>
        wasExcluded ? prev.filter((id) => id !== specId) : [...prev, specId],
      );

      setExcludedDoctorIds((prev) => {
        if (wasExcluded) {
          return prev.filter((id) => !doctorIds.includes(id));
        }
        return Array.from(new Set([...prev, ...doctorIds]));
      });
    },
    [getDoctorIdsBySpec, excludedSpecializationIds],
  );

  const handleToggleDoctor = useCallback(
    (doctorId: string) => {
      setExcludedDoctorIds((prev) => {
        const next = prev.includes(doctorId)
          ? prev.filter((id) => id !== doctorId)
          : [...prev, doctorId];

        syncSpecsFromDoctors(next);
        return next;
      });
    },
    [syncSpecsFromDoctors],
  );

  const filteredDoctors: DoctorInfo[] = useMemo(() => {
    if (!doctorSearch) return catalogDoctors;

    const search = doctorSearch.toLowerCase();
    return catalogDoctors.filter((d) => {
      const fullName =
        `${d.surname ?? ''} ${d.givenName ?? ''} ${d.patronym ?? ''}`.toLowerCase();
      return fullName.includes(search);
    });
  }, [catalogDoctors, doctorSearch]);

  const specializations: SpecializationInfo[] = catalogSpecializations;

  return {
    excludedSpecializationIds,
    setExcludedSpecializationIds,
    excludedDoctorIds,
    setExcludedDoctorIds,
    handleToggleSpecialization,
    handleToggleDoctor,
    doctorSearch,
    setDoctorSearch,
    specFilter,
    setSpecFilter,
    specializations,
    filteredDoctors,
  };
};
